import maturityAssessmentData from '../data/maturityAssessment.json'
import businessScopingData from '../data/businessScoping.json'
import technologyScopingData from '../data/technologyScoping.json'
import industryBenchmarks from '../data/industryBenchmarks.json'
import riskMaturityMapping from '../data/riskMaturityMapping.json'
import gapAnalysisConfig from '../data/gapAnalysisConfig.json'
import industryDomainWeightsData from '../data/industryDomainWeights.json'
import claimsBenchmarks from '../data/claimsBenchmarks.json'
import { runValidationChecks, buildScoringEventData } from './scoringValidator'
import { getMaturityLevelByScore } from './maturityLevels'

// Score mapping for maturity responses (equal intervals for statistical defensibility)
const MATURITY_SCORES = {
  'no': 0,
  'partially': 1,
  'mostly': 2,
  'yes': 3,
  'unknown': 0,   // Unknown = no demonstrated capability, auto-flags for follow-up
  'na': null       // N/A = excluded from scoring (not counted in denominator)
}

// Maximum possible score (Yes = 3)
const MAX_SCORE = 3

// Mapping of context factor keywords to business/technology scoping question IDs
// This maps the "Context questions" field in maturity assessment to actual scoping questions
// Includes both bs-* (business scoping) and ts-* (technology scoping) entries
const CONTEXT_FACTOR_MAP = {
  // Business Scoping (bs-*) entries
  'annual revenue': 'bs-1',
  'workforce size': 'bs-3',
  'remote %': 'bs-6',
  'remote % of staff': 'bs-6',
  'it program ownership': 'bs-28',
  'core business systems documented': 'bs-26',
  'cloud usage': 'bs-31',
  'vendor/msp reliance': 'bs-29',
  'contractor use': 'bs-4',
  'leadership view of ai': 'bs-38',
  'ai governance accountability': 'bs-37',
  'ai procurement & deployment': 'bs-34',
  'ai handling sensitive data': 'bs-36',
  'ai tools in use': 'bs-33',
  'third-party/vendor ai use': 'bs-35',
  'ai procurement practices': 'bs-34',
  'ai with sensitive data': 'bs-36',
  'industry/regulatory environment': 'bs-0',
  'regulatory frameworks': 'bs-20',
  'certification milestones': 'bs-22',
  'primary stakeholders': 'bs-40',
  'data protection/accountability ownership': 'bs-15',
  'regulatory obligations': 'bs-20',
  'stakeholder expectations': 'bs-23',
  'data types': 'dt-1',
  'technology enablement': 'bs-11',
  'executive review cadence': 'bs-19',
  'risk appetite': 'bs-12',
  'security program prioritization': 'bs-13',
  'executive oversight': 'bs-19',
  'budgeting approach': 'bs-18',
  'security priority': 'bs-13',
  'success definition': 'bs-14',
  'mission-critical services': 'bs-8',
  'incident impact scenarios': 'bs-9',
  'incident history': 'bs-17',
  'current tools in use': 'bs-25',
  'critical systems': 'bs-8',
  'system review/upgrade cadence': 'bs-27',
  'industry type': 'bs-7',
  'internal software development activity': 'bs-32',
  'core systems documented': 'bs-26',
  'sensitive/regulated data types': 'dt-1',
  'data/system locations': 'bs-5',
  'international regions': 'bs-5a',
  'international regulatory jurisdictions': 'bs-5a',
  'international operations': 'bs-5a',
  'org mission/priorities': 'bs-10',
  'security ranking': 'bs-13',
  'audit history': 'bs-21',
  'regulatory/customer obligations': 'bs-23',
  'it budget': 'bs-41',
  'it budget allocation': 'bs-41',
  'it spending': 'bs-41',
  'technology investment': 'bs-41',
  'security budget': 'bs-42',
  'security spending': 'bs-42',
  'cybersecurity investment': 'bs-42',
  'security budget allocation': 'bs-42',
  'grc platform usage': 'bs-43',
  'grc platform': 'bs-43',
  'compliance tooling': 'bs-43',
  'compliance platform': 'bs-43',
  'governance risk compliance': 'bs-43',

  // Technology Scoping (ts-*) entries
  'email platform': 'ts-1',
  'email and collaboration platform': 'ts-1',
  'collaboration platform': 'ts-1',
  'productivity suite': 'ts-1',
  'identity provider': 'ts-2',
  'identity and access management': 'ts-2',
  'identity management': 'ts-2',
  'idp platform': 'ts-2',
  'access management solution': 'ts-2',
  'line of business application': 'ts-3',
  'lob application': 'ts-3',
  'ehr system': 'ts-3',
  'core banking platform': 'ts-3',
  'erp system': 'ts-3',
  'crm system': 'ts-3',
  'endpoint protection': 'ts-4',
  'endpoint management': 'ts-4',
  'edr solution': 'ts-4',
  'antivirus solution': 'ts-4',
  'backup solution': 'ts-5',
  'backup and recovery': 'ts-5',
  'disaster recovery': 'ts-5',
  'data backup': 'ts-5',
  'network security': 'ts-6',
  'firewall': 'ts-6',
  'firewall solution': 'ts-6',
  'network firewall': 'ts-6',
  'cloud infrastructure': 'ts-7',
  'hosting environment': 'ts-7',
  'data hosting': 'ts-7',
  'cloud platform': 'ts-7',
  'infrastructure hosting': 'ts-7'
}

/**
 * Calculate context weight for a maturity question based on scoping responses
 * Uses a two-pass matching algorithm:
 * 1. Exact match (O(1) lookup) - highest priority
 * 2. Longest-key-wins substring match - fallback for partial matches
 *
 * @param {string} contextQuestions - Comma-separated list of context factors
 * @param {Object} allContextResponses - Merged business + technology scoping responses
 * @returns {number} - Average context weight multiplier
 */
function calculateContextWeight(contextQuestions, allContextResponses) {
  if (!contextQuestions || !allContextResponses) return 1.0

  const factors = contextQuestions.toLowerCase().split(',').map(f => f.trim())
  const weights = []

  for (const factor of factors) {
    let matchedId = null

    // Priority 1: Exact match (O(1) lookup)
    if (CONTEXT_FACTOR_MAP[factor]) {
      matchedId = CONTEXT_FACTOR_MAP[factor]
    }

    // Priority 2: Longest-key-wins substring match
    if (!matchedId) {
      let bestMatch = null
      let bestLength = 0

      for (const [key, id] of Object.entries(CONTEXT_FACTOR_MAP)) {
        // Check if factor contains key or key contains factor
        if ((factor.includes(key) || key.includes(factor)) && key.length > bestLength) {
          bestMatch = id
          bestLength = key.length
        }
      }
      matchedId = bestMatch
    }

    // Extract weight from matched response
    if (matchedId && allContextResponses[matchedId]) {
      const response = allContextResponses[matchedId]
      const responseValue = typeof response === 'object' ? response.value : response

      // Handle multi-select responses: look up max weight from question option definitions
      if (Array.isArray(responseValue) && responseValue.length > 0) {
        const allQuestions = [...businessScopingData.sections, ...technologyScopingData.sections]
          .flatMap(s => s.questions)
        const question = allQuestions.find(q => q.id === matchedId)
        if (question) {
          const selectedWeights = responseValue
            .map(v => question.options.find(o => o.label === v || o.value === v))
            .filter(Boolean)
            .map(o => o.weight || 1.0)
          if (selectedWeights.length > 0) {
            weights.push(Math.max(...selectedWeights))
          }
        }
      } else {
        // Single-select: use stored weight
        const weight = typeof response === 'object' && response.weight
          ? response.weight
          : 1.0
        weights.push(weight)
      }
    }
  }

  if (weights.length === 0) return 1.0

  // Return average of applicable weights
  return weights.reduce((a, b) => a + b, 0) / weights.length
}

/**
 * Calculate all assessment scores from responses
 * @param {Object} responses - All assessment responses
 * @param {Object} previousScores - Previous scores for regression detection (optional)
 * @returns {Object} - Complete scores object with validation metadata
 */
export function calculateScores(responses, previousScores = null) {
  // Extract industry from business scoping for risk appetite weighting
  const bsIndustryResponse = responses.businessScoping?.['bs-0']
  const industry = typeof bsIndustryResponse === 'object' && bsIndustryResponse?.value
    ? bsIndustryResponse.value
    : (typeof bsIndustryResponse === 'string' ? bsIndustryResponse : null)

  const businessScopingScores = calculateBusinessScopingScores(responses.businessScoping || {})
  const technologyScopingScores = calculateTechnologyScopingScores(responses.technologyScoping || {})
  const riskAppetiteScores = calculateRiskAppetiteScores(responses.riskAppetite || {}, industry)

  // Calculate maturity with merged context from both business and technology scoping
  const maturityScores = calculateMaturityScores(
    responses.maturityAssessment || {},
    responses.businessScoping || {},
    responses.technologyScoping || {}
  )

  const overallScore = calculateOverallScore(maturityScores)

  const scores = {
    maturity: maturityScores,
    riskAppetite: riskAppetiteScores,
    businessScoping: businessScopingScores,
    technologyScoping: technologyScopingScores,
    overall: overallScore
  }

  // Run validation checks and build audit event data
  const validationFlags = runValidationChecks(scores, responses, previousScores)
  const auditEventData = buildScoringEventData(scores, responses)

  return {
    ...scores,
    _validation: validationFlags,
    _auditEventData: auditEventData
  }
}

/**
 * Calculate maturity scores by category with context weighting
 * @param {Object} maturityResponses - Maturity assessment responses
 * @param {Object} businessScopingResponses - Business scoping responses
 * @param {Object} technologyScopingResponses - Technology scoping responses (optional)
 * @returns {Object} - Category scores and details
 */
export function calculateMaturityScores(maturityResponses, businessScopingResponses, technologyScopingResponses = {}) {
  const categoryScores = {}
  const categoryDetails = {}

  // Merge business and technology scoping responses for context weight lookups
  // Filter out engagement driver (ed-*) and org characteristic (oc-*) questions
  const allContextResponses = Object.fromEntries(
    Object.entries({ ...businessScopingResponses, ...technologyScopingResponses })
      .filter(([key]) => !key.startsWith('ed-') && !key.startsWith('oc-'))
  )

  maturityAssessmentData.sections.forEach(section => {
    let totalWeightedScore = 0
    let totalWeight = 0
    const questionResults = []

    section.questions.forEach(question => {
      const responseRaw = maturityResponses[question.id]
      const response = typeof responseRaw === 'object' && responseRaw !== null ? responseRaw.value : responseRaw
      const rawScore = MATURITY_SCORES[response]

      // N/A responses are excluded from scoring entirely
      if (response === 'na' || rawScore === null) {
        questionResults.push({
          id: question.id,
          question: question.question,
          response,
          score: null,
          baseWeight: 0,
          contextWeight: 0,
          combinedWeight: 0,
          weightedScore: 0,
          maxPossible: 0,
          frameworkMapping: question.frameworkMapping,
          excluded: true
        })
        return
      }

      const score = rawScore || 0
      const baseWeight = question.baseWeight || 1.0

      // Calculate context weight from merged scoping responses
      const contextWeight = calculateContextWeight(
        question.contextQuestions,
        allContextResponses
      )

      // Combined weight = base weight * context weight
      const combinedWeight = baseWeight * contextWeight
      const weightedScore = score * combinedWeight

      totalWeightedScore += weightedScore
      totalWeight += combinedWeight * MAX_SCORE // Max possible weighted score

      questionResults.push({
        id: question.id,
        question: question.question,
        response,
        score,
        baseWeight,
        contextWeight,
        combinedWeight,
        weightedScore,
        maxPossible: combinedWeight * MAX_SCORE,
        frameworkMapping: question.frameworkMapping
      })
    })

    // Calculate percentage score (0-100)
    const percentageScore = totalWeight > 0
      ? Math.round((totalWeightedScore / totalWeight) * 100)
      : 0

    // Map to maturity level (1-5 scale)
    const maturityLevel = percentageToMaturityLevel(percentageScore)

    categoryScores[section.id] = {
      name: section.name,
      score: percentageScore,
      maturityLevel,
      rawScore: totalWeightedScore,
      maxScore: totalWeight,
      questionCount: section.questions.length,
      answeredCount: questionResults.filter(q => q.response).length,
      averageContextWeight: questionResults.length > 0
        ? Math.round((questionResults.reduce((sum, q) => sum + q.contextWeight, 0) / questionResults.length) * 100) / 100
        : 1.0
    }

    categoryDetails[section.id] = questionResults
  })

  return { categoryScores, categoryDetails }
}

/**
 * Convert percentage score to maturity level (1-5)
 */
function percentageToMaturityLevel(percentage) {
  const { level, label, color } = getMaturityLevelByScore(percentage)
  return { level, label, color }
}

/**
 * Calculate risk appetite scores with industry-weighted averages
 * @param {Object} responses - Risk appetite responses (ra-0 through ra-7, or legacy ra-0 through ra-9)
 * @param {string|null} industry - Industry name from bs-0 for domain weighting
 */
export function calculateRiskAppetiteScores(responses, industry = null) {
  const domains = {}
  const backwardCompatMerges = []

  // New 8-domain order (consolidated from 10)
  const domainOrder = [
    'Financial', 'Reputation & Trust', 'Regulatory & Compliance',
    'Data Protection & Privacy', 'Security & Technology', 'Operational Resilience',
    'AI & Emerging Technology', 'Third-Party & Supply Chain'
  ]

  // Map ra-0 through ra-7 to new domain names
  Object.entries(responses).forEach(([questionId, value]) => {
    const questionIndex = parseInt(questionId.replace('ra-', ''))
    if (questionIndex < domainOrder.length && typeof value === 'number') {
      domains[domainOrder[questionIndex]] = {
        score: value,
        label: getAppetiteLabel(value)
      }
    }
  })

  // Backward compatibility: handle legacy ra-8 (AI Governance) → merge into AI & Emerging Technology
  const oldAIGov = typeof responses['ra-8'] === 'number' ? responses['ra-8'] : null
  if (oldAIGov !== null) {
    const aiDomain = 'AI & Emerging Technology'
    if (domains[aiDomain]) {
      const originalScore = domains[aiDomain].score
      const avgScore = Math.round(((originalScore + oldAIGov) / 2) * 10) / 10
      domains[aiDomain] = { score: avgScore, label: getAppetiteLabel(avgScore) }
      backwardCompatMerges.push(`${aiDomain}: averaged ra-6 (${originalScore}) with legacy ra-8 (${oldAIGov}) = ${avgScore}`)
    } else {
      domains[aiDomain] = { score: oldAIGov, label: getAppetiteLabel(oldAIGov) }
      backwardCompatMerges.push(`${aiDomain}: used legacy ra-8 (${oldAIGov}), no ra-6 present`)
    }
  }

  // Backward compatibility: handle legacy ra-9 (Data Privacy) → merge into Data Protection & Privacy
  const oldDataPrivacy = typeof responses['ra-9'] === 'number' ? responses['ra-9'] : null
  if (oldDataPrivacy !== null) {
    const dataDomain = 'Data Protection & Privacy'
    if (domains[dataDomain]) {
      const originalScore = domains[dataDomain].score
      const avgScore = Math.round(((originalScore + oldDataPrivacy) / 2) * 10) / 10
      domains[dataDomain] = { score: avgScore, label: getAppetiteLabel(avgScore) }
      backwardCompatMerges.push(`${dataDomain}: averaged ra-3 (${originalScore}) with legacy ra-9 (${oldDataPrivacy}) = ${avgScore}`)
    } else {
      domains[dataDomain] = { score: oldDataPrivacy, label: getAppetiteLabel(oldDataPrivacy) }
      backwardCompatMerges.push(`${dataDomain}: used legacy ra-9 (${oldDataPrivacy}), no ra-3 present`)
    }
  }

  // Industry-weighted average calculation
  const industryWeights = (industry && industryDomainWeightsData.weights[industry])
    || industryDomainWeightsData.default

  let weightedSum = 0
  let totalWeight = 0
  const domainWeightDetails = []

  Object.entries(domains).forEach(([domain, data]) => {
    const weight = industryWeights[domain] || 1.0
    weightedSum += data.score * weight
    totalWeight += weight
    domainWeightDetails.push({
      domain,
      score: data.score,
      industryWeight: weight,
      contribution: Math.round((data.score * weight) * 100) / 100
    })
  })

  const weightedAverage = totalWeight > 0
    ? Math.round((weightedSum / totalWeight) * 10) / 10
    : 0

  // Simple average for comparison / audit
  const values = Object.values(domains).map(d => d.score).filter(v => v)
  const simpleAverage = values.length > 0
    ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10
    : 0

  return {
    domains,
    averageAppetite: weightedAverage,
    averageLabel: getAppetiteLabel(weightedAverage),
    simpleAverage,
    industryWeightsApplied: !!industry,
    industry: industry || null,
    domainWeightDetails,
    backwardCompatMerges,
    domainCount: Object.keys(domains).length
  }
}

/**
 * Get appetite label from score
 */
function getAppetiteLabel(score) {
  if (score >= 4.5) return 'Very High'
  if (score >= 3.5) return 'High'
  if (score >= 2.5) return 'Moderate'
  if (score >= 1.5) return 'Low'
  return 'Very Low'
}

/**
 * Calculate business scoping context profile
 * Note: Engagement driver questions (ed-*) are excluded from context weight calculations
 * as they influence roadmap/threat profiles, not maturity scoring
 */
export function calculateBusinessScopingScores(responses) {
  const profile = {}
  const categoryWeights = {}

  // Group responses by category and calculate average weights
  // Skip sections/questions marked with excludeFromScoring
  businessScopingData.sections.forEach(section => {
    // Skip engagement context section entirely - it doesn't affect maturity scoring
    if (section.excludeFromScoring || section.id === 'engagement-context') {
      return
    }

    const weights = []
    section.questions.forEach(q => {
      // Skip engagement driver (ed-*) and org characteristic (oc-*) questions - they don't affect context weights
      if (q.excludeFromScoring || q.id.startsWith('ed-') || q.id.startsWith('oc-')) {
        return
      }

      const response = responses[q.id]
      if (response && typeof response === 'object' && response.weight) {
        weights.push(response.weight)
      }
    })

    if (weights.length > 0) {
      categoryWeights[section.name] = {
        averageWeight: Math.round((weights.reduce((a, b) => a + b, 0) / weights.length) * 100) / 100,
        responseCount: weights.length
      }
    }
  })

  // Calculate overall context weight
  const allWeights = Object.values(categoryWeights).map(c => c.averageWeight)
  const overallWeight = allWeights.length > 0
    ? Math.round((allWeights.reduce((a, b) => a + b, 0) / allWeights.length) * 100) / 100
    : 1.0

  return {
    categoryWeights,
    overallContextWeight: overallWeight,
    responseCount: Object.keys(responses).filter(k => !k.startsWith('ed-') && !k.startsWith('oc-')).length
  }
}

/**
 * Calculate technology scoping context profile
 * Extracts platform information and calculates average context weights
 */
export function calculateTechnologyScopingScores(responses) {
  const categoryWeights = {}
  const platformSummary = {}

  // Group responses by section and calculate average weights
  technologyScopingData.sections.forEach(section => {
    const weights = []
    const platforms = []

    section.questions.forEach(q => {
      const response = responses[q.id]
      if (response) {
        // Extract weight
        if (typeof response === 'object' && response.weight) {
          weights.push(response.weight)
        }
        // Extract platform value for summary
        const value = typeof response === 'object' ? response.value : response
        if (value) {
          platforms.push({
            questionId: q.id,
            category: q.category,
            value: value,
            platformHint: q.options?.find(opt => opt.label === value)?.platformHint || null
          })
        }
      }
    })

    if (weights.length > 0) {
      categoryWeights[section.name] = {
        averageWeight: Math.round((weights.reduce((a, b) => a + b, 0) / weights.length) * 100) / 100,
        responseCount: weights.length
      }
    }

    // Add platforms to summary
    platforms.forEach(p => {
      platformSummary[p.questionId] = p
    })
  })

  // Calculate overall context weight
  const allWeights = Object.values(categoryWeights).map(c => c.averageWeight)
  const overallWeight = allWeights.length > 0
    ? Math.round((allWeights.reduce((a, b) => a + b, 0) / allWeights.length) * 100) / 100
    : 1.0

  // Determine primary platform ecosystem (Microsoft, Google, mixed, etc.)
  const platformHints = Object.values(platformSummary)
    .map(p => p.platformHint)
    .filter(h => h && h !== 'unknown' && h !== 'other' && h !== 'none')

  let primaryEcosystem = 'mixed'
  if (platformHints.length > 0) {
    const hintCounts = platformHints.reduce((acc, hint) => {
      acc[hint] = (acc[hint] || 0) + 1
      return acc
    }, {})
    const sortedHints = Object.entries(hintCounts).sort((a, b) => b[1] - a[1])
    if (sortedHints[0] && sortedHints[0][1] >= 3) {
      primaryEcosystem = sortedHints[0][0]
    }
  }

  return {
    categoryWeights,
    overallContextWeight: overallWeight,
    responseCount: Object.keys(responses).length,
    platformSummary,
    primaryEcosystem
  }
}

/**
 * Calculate overall maturity score
 */
export function calculateOverallScore(maturityScores) {
  const categories = Object.values(maturityScores.categoryScores)
  if (categories.length === 0) return { score: 0, maturityLevel: percentageToMaturityLevel(0) }

  const totalScore = categories.reduce((sum, cat) => sum + cat.score, 0)
  const averageScore = Math.round(totalScore / categories.length)

  return {
    score: averageScore,
    maturityLevel: percentageToMaturityLevel(averageScore),
    categoryCount: categories.length,
    strongestCategories: categories
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map(c => ({ name: c.name, score: c.score })),
    weakestCategories: categories
      .sort((a, b) => a.score - b.score)
      .slice(0, 3)
      .map(c => ({ name: c.name, score: c.score }))
  }
}

/**
 * Get recommendations based on scores
 */
export function getRecommendations(scores) {
  const recommendations = []

  // Add recommendations for weak categories
  if (scores.overall.weakestCategories) {
    scores.overall.weakestCategories.forEach(cat => {
      if (cat.score < 40) {
        recommendations.push({
          priority: 'high',
          category: cat.name,
          message: `${cat.name} scored ${cat.score}% and requires immediate attention.`,
          type: 'maturity'
        })
      } else if (cat.score < 60) {
        recommendations.push({
          priority: 'medium',
          category: cat.name,
          message: `${cat.name} scored ${cat.score}% and has room for improvement.`,
          type: 'maturity'
        })
      }
    })
  }

  // Add recommendations for risk appetite misalignment
  if (scores.riskAppetite.domains) {
    Object.entries(scores.riskAppetite.domains).forEach(([domain, data]) => {
      if (data.score >= 4) {
        recommendations.push({
          priority: 'info',
          category: domain,
          message: `High risk appetite in ${domain} - ensure controls match tolerance level.`,
          type: 'risk'
        })
      } else if (data.score <= 2) {
        recommendations.push({
          priority: 'info',
          category: domain,
          message: `Low risk appetite in ${domain} - prioritize strong controls in this area.`,
          type: 'risk'
        })
      }
    })
  }

  // Add recommendations based on context weights
  if (scores.businessScoping?.overallContextWeight > 1.3) {
    recommendations.push({
      priority: 'info',
      category: 'Business Context',
      message: `Your organization's context profile indicates elevated risk factors (weight: ${scores.businessScoping.overallContextWeight}x). Consider additional controls.`,
      type: 'context'
    })
  }

  return recommendations.sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, info: 2 }
    return priorityOrder[a.priority] - priorityOrder[b.priority]
  })
}

/**
 * Get industry benchmark data
 */
export function getIndustryBenchmark(industry) {
  return industryBenchmarks.benchmarks[industry] || industryBenchmarks.benchmarks['Other']
}

/**
 * Resolve an industry name to claims benchmark sector profile data.
 * Maps industryBenchmarks.json industry keys to claimsBenchmarks.json sectorProfiles.
 * Returns null if no match found (claims enrichment is optional).
 */
function getClaimsContextForIndustry(industry) {
  if (!industry || !claimsBenchmarks.sectorProfiles) return null

  const profile = claimsBenchmarks.sectorProfiles[industry]
  if (profile && industry !== '_doc') {
    return { key: industry, ...profile }
  }

  // Handle known aliases between industryBenchmarks and claimsBenchmarks sector keys
  const aliases = {
    'Legal': 'Legal'
  }
  const aliasKey = aliases[industry]
  if (aliasKey && claimsBenchmarks.sectorProfiles[aliasKey]) {
    return { key: aliasKey, ...claimsBenchmarks.sectorProfiles[aliasKey] }
  }

  return null
}

/**
 * Map a topCausesOfLoss string label to a causeOfLoss key in claimsBenchmarks.
 */
function mapCauseLabelToKey(label) {
  const labelMap = {
    'Ransomware': 'ransomware',
    'BEC': 'bec',
    'Business Email Compromise': 'bec',
    'Hackers': 'hacker',
    'Hacker': 'hacker',
    'Wire Transfer Fraud': 'wireTransferFraud',
    'Theft of Money': 'theftOfMoney',
    'Staff Mistakes': 'staffMistakes',
    'Rogue Employees': 'rogueEmployees',
    'Third-Party': 'thirdPartyMalicious'
  }
  return labelMap[label] || null
}

/**
 * Get market segment modifier based on organization size
 * @param {string} marketSegment - One of: micro, small, mid-market, upper-mid-market
 * @returns {Object} Market segment data including modifier
 */
export function getMarketSegmentModifier(marketSegment) {
  const normalizedSegment = (marketSegment || 'mid-market').toLowerCase().replace(/\s+/g, '-')
  return industryBenchmarks.marketSegmentModifiers[normalizedSegment] ||
    industryBenchmarks.marketSegmentModifiers['mid-market']
}

/**
 * Calculate benchmark gap and severity
 * @param {number} clientScore - Client's maturity score
 * @param {string} industry - Industry vertical
 * @param {string} marketSegment - Optional market segment for size-adjusted benchmark
 */
export function calculateBenchmarkGap(clientScore, industry, marketSegment = 'mid-market') {
  const benchmark = getIndustryBenchmark(industry)
  const segmentData = getMarketSegmentModifier(marketSegment)

  // Apply market segment modifier to benchmark
  const adjustedBenchmark = Math.round(benchmark.maturityBenchmark * segmentData.benchmarkModifier)

  const gap = clientScore - adjustedBenchmark
  const gapPercentage = Math.round(gap * 10) / 10

  // Determine severity based on gap thresholds
  let severity
  const thresholds = industryBenchmarks.gapThresholds
  if (gap >= thresholds.above.min) {
    severity = thresholds.above
  } else if (gap >= thresholds.at.min) {
    severity = thresholds.at
  } else if (gap >= thresholds.below.min) {
    severity = thresholds.below
  } else {
    severity = thresholds.critical
  }

  // Look up claims benchmark data for this industry
  const claimsSector = getClaimsContextForIndustry(industry)

  // Resolve top cause of loss label to full cause data
  let topCauseData = null
  if (claimsSector?.topCausesOfLoss?.[0]) {
    const topCauseKey = mapCauseLabelToKey(claimsSector.topCausesOfLoss[0])
    if (topCauseKey && claimsBenchmarks.causeOfLoss?.[topCauseKey]) {
      topCauseData = claimsBenchmarks.causeOfLoss[topCauseKey]
    }
  }

  return {
    benchmark: adjustedBenchmark,
    baseBenchmark: benchmark.maturityBenchmark,
    benchmarkRange: benchmark.benchmarkRange,
    clientScore,
    gap: gapPercentage,
    severity,
    industryInfo: benchmark,
    marketSegment: segmentData,
    sourceInfo: {
      primarySource: benchmark.primarySource,
      sourceNotes: benchmark.sourceNotes,
      lastUpdated: industryBenchmarks.metadata?.lastUpdated,
      sources: industryBenchmarks.sources
    },
    claimsContext: claimsSector ? {
      avgIncidentCost: claimsSector.incidentCost?.fiveYearAverage || null,
      avgCrisisServicesCost: claimsSector.crisisServices?.total || null,
      topCauseOfLoss: claimsSector.topCausesOfLoss?.[0] || null,
      topCauseAvgCost: topCauseData?.averageIncidentCost || null,
      claimsCount: claimsSector.claimsCount,
      percentOfTotalCost: claimsSector.percentOfTotalCost,
      source: claimsSector.source,
      dataWindow: claimsBenchmarks.metadata?.dataWindow || '2020-2024'
    } : null
  }
}

/**
 * Calculate risk-maturity alignment for each domain
 */
export function calculateRiskMaturityAlignment(riskAppetiteScores, maturityScores) {
  const alignments = []
  const mappings = riskMaturityMapping.domainMappings
  const thresholds = riskMaturityMapping.alignmentThresholds
  const appetiteExpectations = riskMaturityMapping.appetiteToMaturityExpectation

  Object.entries(riskAppetiteScores.domains || {}).forEach(([domain, riskData]) => {
    const relatedCategories = mappings[domain] || []
    if (relatedCategories.length === 0) return

    // Calculate average maturity of related categories
    let totalMaturity = 0
    let categoryCount = 0
    const categoryDetails = []

    relatedCategories.forEach(categoryId => {
      const categoryScore = maturityScores.categoryScores?.[categoryId]
      if (categoryScore) {
        totalMaturity += categoryScore.score
        categoryCount++
        categoryDetails.push({
          id: categoryId,
          name: categoryScore.name,
          score: categoryScore.score
        })
      }
    })

    const avgMaturity = categoryCount > 0 ? Math.round(totalMaturity / categoryCount) : 0

    // Determine expected minimum maturity based on risk appetite
    let expectedMinMaturity = 60 // default
    if (riskData.score >= 4.5) {
      expectedMinMaturity = appetiteExpectations.veryHigh.expectedMinMaturity
    } else if (riskData.score >= 3.5) {
      expectedMinMaturity = appetiteExpectations.high.expectedMinMaturity
    } else if (riskData.score >= 2.5) {
      expectedMinMaturity = appetiteExpectations.moderate.expectedMinMaturity
    } else if (riskData.score >= 1.5) {
      expectedMinMaturity = appetiteExpectations.low.expectedMinMaturity
    } else {
      expectedMinMaturity = appetiteExpectations.veryLow.expectedMinMaturity
    }

    const gap = avgMaturity - expectedMinMaturity

    let alignment
    if (gap >= -thresholds.aligned.maxGap) {
      alignment = thresholds.aligned
    } else if (gap >= -thresholds.caution.maxGap) {
      alignment = thresholds.caution
    } else {
      alignment = thresholds.misaligned
    }

    // Flag as concern if low appetite (risk averse) with low maturity
    const isConcern = riskData.score <= 2 && avgMaturity < 50

    alignments.push({
      domain,
      riskAppetite: riskData,
      relatedMaturity: {
        average: avgMaturity,
        categories: categoryDetails
      },
      expectedMinMaturity,
      gap,
      alignment,
      isConcern
    })
  })

  return alignments.sort((a, b) => a.gap - b.gap) // Sort by gap (worst first)
}

/**
 * Get top 3 actionable priorities with effort/impact indicators
 */
export function getTopPriorities(scores, alignments, benchmarkGap) {
  const priorities = []

  // Priority 1: Most misaligned risk-maturity domains
  const misalignedDomains = alignments.filter(a => a.isConcern || a.alignment.label === 'Misaligned')
  misalignedDomains.slice(0, 1).forEach(domain => {
    priorities.push({
      type: 'alignment',
      title: `Address ${domain.domain} Risk-Maturity Gap`,
      description: `Risk appetite is ${domain.riskAppetite.label.toLowerCase()} but maturity is only ${domain.relatedMaturity.average}%. Focus on ${domain.relatedMaturity.categories[0]?.name || 'related controls'}.`,
      effort: domain.relatedMaturity.average < 25 ? 'High' : 'Medium',
      impact: 'High',
      urgency: 'Immediate'
    })
  })

  // Priority 2: Weakest maturity categories
  const weakCategories = scores.overall.weakestCategories || []
  weakCategories.slice(0, 1).forEach(cat => {
    if (cat.score < 40) {
      priorities.push({
        type: 'maturity',
        title: `Improve ${cat.name}`,
        description: `Current score of ${cat.score}% indicates significant gaps. Implement foundational controls and processes.`,
        effort: cat.score < 20 ? 'High' : 'Medium',
        impact: 'High',
        urgency: cat.score < 20 ? 'Immediate' : 'Short-term'
      })
    }
  })

  // Priority 3: Benchmark gap closure
  if (benchmarkGap.severity.severity === 'critical' || benchmarkGap.severity.severity === 'warning') {
    priorities.push({
      type: 'benchmark',
      title: 'Close Industry Benchmark Gap',
      description: benchmarkGap.claimsContext?.avgIncidentCost
        ? `Currently ${Math.abs(benchmarkGap.gap)}% below ${benchmarkGap.industryInfo.description} benchmark of ${benchmarkGap.benchmark}%. Organizations in this sector face an average incident cost of $${formatNumber(benchmarkGap.claimsContext.avgIncidentCost)}${benchmarkGap.claimsContext.topCauseOfLoss ? `, primarily driven by ${benchmarkGap.claimsContext.topCauseOfLoss.toLowerCase()}` : ''}.`
        : `Currently ${Math.abs(benchmarkGap.gap)}% below ${benchmarkGap.industryInfo.description} benchmark of ${benchmarkGap.benchmark}%.`,
      effort: Math.abs(benchmarkGap.gap) > 15 ? 'High' : 'Medium',
      impact: 'Medium',
      urgency: 'Medium-term'
    })
  }

  // Fill remaining slots with general recommendations
  if (priorities.length < 3) {
    const highRiskDomains = Object.entries(scores.riskAppetite.domains || {})
      .filter(([_, data]) => data.score >= 4)
      .slice(0, 3 - priorities.length)

    highRiskDomains.forEach(([domain, data]) => {
      priorities.push({
        type: 'risk',
        title: `Monitor ${domain} Risk Exposure`,
        description: `High risk tolerance (${data.score}/5) requires robust monitoring and controls to manage exposure.`,
        effort: 'Low',
        impact: 'Medium',
        urgency: 'Ongoing'
      })
    })
  }

  // If still need more, add from strongest categories for maintenance
  if (priorities.length < 3) {
    const strongCategories = scores.overall.strongestCategories || []
    strongCategories.slice(0, 3 - priorities.length).forEach(cat => {
      if (cat.score >= 60) {
        priorities.push({
          type: 'maintain',
          title: `Maintain ${cat.name} Excellence`,
          description: `Strong score of ${cat.score}%. Continue current practices and monitor for regressions.`,
          effort: 'Low',
          impact: 'Low',
          urgency: 'Ongoing'
        })
      }
    })
  }

  return priorities.slice(0, 3)
}

/**
 * Determine organization size based on business scoping responses
 */
function getOrganizationSize(businessScopingResponses) {
  const revenue = businessScopingResponses?.['bs-1']?.value || ''
  const workforce = businessScopingResponses?.['bs-3']?.value || ''

  // Check workforce first
  if (workforce.includes('250+') || workforce.includes('1000+')) return 'large'
  if (workforce.includes('51-250')) return 'medium'

  // Check revenue (supports both new and legacy labels)
  if (revenue.includes('Over $500M') || revenue.includes('>$500mm') || revenue.includes('>$100mm')) return 'enterprise'
  if (revenue.includes('$250M') || revenue.includes('$100M') || revenue.includes('$50mm')) return 'large'
  if (revenue.includes('$25M') || revenue.includes('$5mm-$25mm')) return 'medium'

  return 'small'
}

/**
 * Determine gap severity based on gap value
 */
function getGapSeverity(gap) {
  const severities = gapAnalysisConfig.gapSeverity
  if (gap >= 0) return severities.aligned
  if (gap >= -10) return severities.low
  if (gap >= -20) return severities.moderate
  if (gap >= -35) return severities.high
  return severities.critical
}

/**
 * Calculate estimated remediation cost range.
 * When claims data is available, blends config-based estimate with claims-backed data.
 */
function calculateCostRange(severity, domain, orgSize, claimsAvgIncidentCost = null) {
  const baseCosts = gapAnalysisConfig.costEstimates.byOrganizationSize[orgSize]?.baseCosts
  if (!baseCosts) return null

  const severityKey = severity.label.toLowerCase().replace(' gap', '').replace(' ', '')
  const baseCost = baseCosts[severityKey] || baseCosts.moderate

  const domainMultiplier = gapAnalysisConfig.costEstimates.domainMultipliers[domain] || 1.0

  let adjustedMin = Math.round(baseCost.min * domainMultiplier)
  let adjustedMax = Math.round(baseCost.max * domainMultiplier)
  let claimsValidated = false

  // When claims data available, blend config estimate with claims-backed cost
  if (claimsAvgIncidentCost && claimsAvgIncidentCost > 0) {
    const severityScaleFactor = {
      'critical': 1.0,
      'significant': 0.7,
      'moderate': 0.4,
      'minor': 0.2,
      'aligned': 0.05
    }
    const scaleFactor = severityScaleFactor[severityKey] || 0.4
    const claimsScaledCost = Math.round(claimsAvgIncidentCost * scaleFactor)

    // Blend: 40% config-based, 60% claims-based for the max
    adjustedMax = Math.round((adjustedMax * 0.4) + (claimsScaledCost * 0.6))
    adjustedMin = Math.min(adjustedMin, adjustedMax)
    claimsValidated = true
  }

  return {
    min: adjustedMin,
    max: adjustedMax,
    formatted: `$${formatNumber(adjustedMin)} - $${formatNumber(adjustedMax)}`,
    claimsValidated
  }
}

/**
 * Format number with commas
 */
function formatNumber(num) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * Get recommended timeframe based on severity
 */
function getRecommendedTimeframe(severity) {
  const timeframes = gapAnalysisConfig.remediationTimeframes
  const severityLabel = severity.label.toLowerCase().replace(' gap', '').replace(' ', '')

  for (const [key, timeframe] of Object.entries(timeframes)) {
    if (timeframe.applicableGaps.includes(severityLabel)) {
      return timeframe
    }
  }
  return timeframes.ongoing
}

/**
 * Calculate gap impact estimates for a single alignment
 * @param {Object} alignment - Single alignment object from calculateRiskMaturityAlignment
 * @param {string} industry - Industry for context
 * @param {string} orgSize - Organization size (small, medium, large, enterprise)
 * @returns {Object} Impact estimates
 */
export function calculateGapImpact(alignment, industry, orgSize = 'medium') {
  const severity = getGapSeverity(alignment.gap)
  const claimsSector = getClaimsContextForIndustry(industry)
  const claimsAvgCost = claimsSector?.incidentCost?.fiveYearAverage || null
  const costRange = calculateCostRange(severity, alignment.domain, orgSize, claimsAvgCost)
  const timeframe = getRecommendedTimeframe(severity)

  // Get domain-specific business impact description
  const impactDescriptions = gapAnalysisConfig.businessImpactDescriptions[alignment.domain]
  const severityKey = severity.label.toLowerCase().replace(' gap', '').replace(' ', '')
  const businessImpact = impactDescriptions?.[severityKey] ||
    `${severity.label} in ${alignment.domain} requiring ${timeframe.description.toLowerCase()} remediation.`

  // Get recommended actions
  const recommendedActions = gapAnalysisConfig.recommendedActions[severityKey] || []

  return {
    domain: alignment.domain,
    gap: alignment.gap,
    severity,
    businessImpact,
    costRange,
    timeframe,
    recommendedActions,
    impactCategories: {
      financial: severity.impactMultiplier >= 1.5 ? 'High' : severity.impactMultiplier >= 1.0 ? 'Medium' : 'Low',
      reputation: alignment.domain === 'Reputation & Trust' ? 'High' : severity.impactMultiplier >= 1.5 ? 'Medium' : 'Low',
      legal: ['Regulatory & Compliance', 'Data Protection & Privacy'].includes(alignment.domain) ? 'High' : severity.impactMultiplier >= 1.5 ? 'Medium' : 'Low',
      operational: ['Operational Resilience', 'Security & Technology'].includes(alignment.domain) ? 'High' : severity.impactMultiplier >= 1.0 ? 'Medium' : 'Low'
    }
  }
}

/**
 * Generate enhanced gap analysis with business context for all alignments
 * @param {Array} alignments - Array of alignment objects from calculateRiskMaturityAlignment
 * @param {string} industry - Industry for context
 * @param {Object} businessScopingResponses - Business scoping responses for org size
 * @returns {Object} Enhanced gap analysis
 */
export function getEnhancedGapAnalysis(alignments, industry, businessScopingResponses = {}) {
  const orgSize = getOrganizationSize(businessScopingResponses)

  // Calculate impact for each alignment
  const impacts = alignments.map(alignment => calculateGapImpact(alignment, industry, orgSize))

  // Separate by severity
  const criticalGaps = impacts.filter(i => i.severity.label === 'Critical Gap')
  const highGaps = impacts.filter(i => i.severity.label === 'Significant Gap')
  const moderateGaps = impacts.filter(i => i.severity.label === 'Moderate Gap')
  const lowGaps = impacts.filter(i => i.severity.label === 'Minor Gap')
  const alignedDomains = impacts.filter(i => i.severity.label === 'Aligned')

  // Calculate total estimated cost range
  const totalMinCost = impacts.reduce((sum, i) => sum + (i.costRange?.min || 0), 0)
  const totalMaxCost = impacts.reduce((sum, i) => sum + (i.costRange?.max || 0), 0)

  // Determine overall urgency
  let overallUrgency = 'Ongoing'
  if (criticalGaps.length > 0) overallUrgency = 'Immediate'
  else if (highGaps.length > 0) overallUrgency = 'Short-term'
  else if (moderateGaps.length > 0) overallUrgency = 'Medium-term'
  else if (lowGaps.length > 0) overallUrgency = 'Long-term'

  // Claims-backed exposure context
  const claimsSector = getClaimsContextForIndustry(industry)
  const claimsExposure = claimsSector ? {
    sectorAvgIncidentCost: claimsSector.incidentCost?.fiveYearAverage || null,
    sectorClaimsCount: claimsSector.claimsCount,
    topCausesOfLoss: claimsSector.topCausesOfLoss || [],
    source: claimsSector.source,
    dataWindow: claimsBenchmarks.metadata?.dataWindow || '2020-2024'
  } : null

  return {
    impacts,
    summary: {
      criticalCount: criticalGaps.length,
      highCount: highGaps.length,
      moderateCount: moderateGaps.length,
      lowCount: lowGaps.length,
      alignedCount: alignedDomains.length,
      totalDomains: impacts.length,
      overallUrgency,
      estimatedTotalCost: {
        min: totalMinCost,
        max: totalMaxCost,
        formatted: `$${formatNumber(totalMinCost)} - $${formatNumber(totalMaxCost)}`
      },
      claimsExposure
    },
    criticalGaps,
    highGaps,
    moderateGaps,
    lowGaps,
    alignedDomains,
    organizationSize: orgSize,
    industry
  }
}
